import { Head, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { UserPlus } from "lucide-react"

interface Invitation {
  email: string
  organization_name: string
  inviter_name: string
  expires_at: string
  token: string
}

interface Props {
  invitation: Invitation
}

export default function InvitationShow({ invitation }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    password: "",
    password_confirmation: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/invitations/${invitation.token}/accept`)
  }

  const expiresAt = new Date(invitation.expires_at)

  return (
    <>
      <Head title="Invitación" />

      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-teal-50 to-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
                <UserPlus className="text-primary h-8 w-8" />
              </div>
            </div>
            <CardTitle className="text-2xl">Has sido invitado</CardTitle>
            <CardDescription>
              <strong>{invitation.inviter_name}</strong> te ha invitado a unirte
              a <strong>{invitation.organization_name}</strong>
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={invitation.email}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nombre completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder="Tu nombre"
                  required
                />
                {errors.name && (
                  <p className="text-destructive text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={(e) => setData("password", e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
                {errors.password && (
                  <p className="text-destructive text-sm">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">
                  Confirmar contraseña
                </Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={data.password_confirmation}
                  onChange={(e) =>
                    setData("password_confirmation", e.target.value)
                  }
                  placeholder="Confirma tu contraseña"
                  required
                />
                {errors.password_confirmation && (
                  <p className="text-destructive text-sm">
                    {errors.password_confirmation}
                  </p>
                )}
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="text-amber-800">
                  ⏰ Esta invitación expira el{" "}
                  {expiresAt.toLocaleDateString("es", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={processing}>
                {processing
                  ? "Creando cuenta..."
                  : "Aceptar invitación y crear cuenta"}
              </Button>
            </form>

            <div className="text-muted-foreground mt-4 text-center text-sm">
              <p>
                ¿Ya tienes una cuenta?{" "}
                <a href="/sign_in" className="text-primary hover:underline">
                  Inicia sesión
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
